import { createContext, useContext, useState } from 'react';

import {
  useGetExtractedTable,
  type ExtractedTableJson,
  type ExtractedTableType,
} from '@frontend/api';
import { noop } from '@frontend/utils';

import { useExtractedTables } from './ExtractedTableContext';

export type SelectedTableContextType = {
  setSelectedTableUrl: (val: string | null) => void;
  selectedTableUrl: string | null;
  isSelectedTableFetching: boolean;
  rawTableData: ExtractedTableJson | null;
  selectedExtractedTable?: ExtractedTableType | null;
  hasTableDataError: boolean;
};

export const DEFAULT_CONTEXT: SelectedTableContextType = {
  setSelectedTableUrl: noop,
  selectedTableUrl: null,
  rawTableData: null,
  selectedExtractedTable: null,
  isSelectedTableFetching: false,
  hasTableDataError: false,
};

export const SelectedTableContext =
  createContext<SelectedTableContextType>(DEFAULT_CONTEXT);

type ExtractedTableProviderProps = {
  children: React.ReactNode;
};

/**
 * Handles selecting a table from the list of extracted tables and pulling the relevant data
 */
export const SelectedTableProvider = ({
  children,
}: ExtractedTableProviderProps) => {
  const { extractedTables } = useExtractedTables();
  const [selectedTableUrl, setSelectedTableUrl] = useState<string | null>(null);

  // Pull the specific selected table data, if we have a selected URL
  const {
    data: selectedExtractedTable,
    // In react-query v4, disabled queries report isLoading=true. Use
    // isInitialLoading to preserve v3 behaviour (false when disabled).
    isInitialLoading: isTableDataLoading,
    isFetching: isTableDataFetching,
    isError: hasTableDataError,
  } = useGetExtractedTable(selectedTableUrl ?? '', {
    queryOptions: { enabled: !!selectedTableUrl },
  });

  // Pull the specific selected table data
  const rawTableData =
    extractedTables?.find(
      (table) => table.authenticatedUrl === selectedTableUrl,
    ) ?? null;

  return (
    <SelectedTableContext.Provider
      value={{
        setSelectedTableUrl,
        selectedTableUrl,
        hasTableDataError,
        isSelectedTableFetching: isTableDataLoading || isTableDataFetching,
        rawTableData,
        selectedExtractedTable,
      }}
    >
      {children}
    </SelectedTableContext.Provider>
  );
};

export const useSelectedTable = () => {
  return useContext<SelectedTableContextType>(SelectedTableContext);
};
